/**
 * Categories Settings Page
 *
 * Inline editing with auto-save. No modals, no save buttons.
 * - Color dots next to category names
 * - Small color picker on click
 * - Debounced auto-save as you type
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Typography,
  Input,
  ColorPicker,
  Popconfirm,
  Spin,
  Empty,
  Tooltip,
  Switch,
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { categoriesApi } from '@api/index';
import type { Category } from '@/types';
import { useAutoSave } from '@hooks/useAutoSave';

const { Title, Text } = Typography;

interface EditableCategory extends Category {
  isNew?: boolean;
  templateCount?: number;
}

// Individual category row with inline editing
function CategoryRow({
  category,
  onUpdate,
  onDelete,
  onCreated,
}: {
  category: EditableCategory;
  onUpdate: (id: string, data: Partial<EditableCategory>) => Promise<void>;
  onDelete: (id: string) => void;
  onCreated?: (category: Category) => void;
}) {
  const [name, setName] = useState(category.name);
  const [description, setDescription] = useState(category.description || '');
  const [color, setColor] = useState(category.color || '#1890ff');
  const [isActive, setIsActive] = useState(category.isActive);
  const [isCreating, setIsCreating] = useState(false);
  const inputRef = useRef<any>(null);
  const queryClient = useQueryClient();

  // Auto-save for updates
  const { save, status } = useAutoSave({
    saveFn: async (data: Partial<EditableCategory>) => {
      await onUpdate(category.id, data);
    },
    queryKey: 'categories',
    debounceMs: 600,
  });

  // Create mutation for new categories
  const createMutation = useMutation({
    mutationFn: async (data: { name: string; color: string; description?: string }) => {
      return categoriesApi.create({
        name: data.name,
        description: data.description,
        color: data.color,
        isActive: true,
      });
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      onCreated?.(created);
      // Reset the row for new entry
      setName('');
      setDescription('');
      setColor('#1890ff');
      setIsCreating(false);
    },
    onError: () => {
      setIsCreating(false);
    },
  });

  // Focus input for new rows
  useEffect(() => {
    if (category.isNew && inputRef.current) {
      inputRef.current.focus();
    }
  }, [category.isNew]);

  const handleNameChange = useCallback(
    (value: string) => {
      setName(value);
      if (!category.isNew && value.trim()) {
        save({ name: value });
      }
    },
    [category.isNew, save]
  );

  const handleDescriptionChange = useCallback(
    (value: string) => {
      setDescription(value);
      if (!category.isNew) {
        save({ description: value });
      }
    },
    [category.isNew, save]
  );

  const handleColorChange = useCallback(
    (newColor: any) => {
      const hexColor = typeof newColor === 'string' ? newColor : newColor?.toHexString?.() || '#1890ff';
      setColor(hexColor);
      if (!category.isNew) {
        save({ color: hexColor });
      }
    },
    [category.isNew, save]
  );

  const handleActiveChange = useCallback(
    (checked: boolean) => {
      setIsActive(checked);
      if (!category.isNew) {
        save({ isActive: checked });
      }
    },
    [category.isNew, save]
  );

  const handleNameBlur = useCallback(() => {
    // For new categories, create on blur if name is filled
    if (category.isNew && name.trim() && !isCreating) {
      setIsCreating(true);
      createMutation.mutate({ name: name.trim(), color, description: description.trim() || undefined });
    }
  }, [category.isNew, name, color, description, isCreating, createMutation]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && category.isNew && name.trim() && !isCreating) {
        setIsCreating(true);
        createMutation.mutate({ name: name.trim(), color, description: description.trim() || undefined });
      }
    },
    [category.isNew, name, color, description, isCreating, createMutation]
  );

  // Status indicator
  const StatusIcon = () => {
    if (isCreating || createMutation.isPending) {
      return <SyncOutlined spin style={{ color: '#1890ff', fontSize: 12 }} />;
    }
    switch (status) {
      case 'pending':
      case 'saving':
        return <SyncOutlined spin style={{ color: '#1890ff', fontSize: 12 }} />;
      case 'saved':
        return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />;
      case 'error':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 12 }} />;
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 0',
        borderBottom: '1px solid #f0f0f0',
      }}
    >
      {/* Color dot with picker */}
      <ColorPicker
        value={color}
        onChange={handleColorChange}
        size="small"
        presets={[
          {
            label: 'Colors',
            colors: [
              '#1890ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1',
              '#eb2f96', '#13c2c2', '#fa8c16', '#a0d911', '#2f54eb',
            ],
          },
        ]}
      >
        <div
          style={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            backgroundColor: color,
            cursor: 'pointer',
            border: '2px solid #fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            flexShrink: 0,
          }}
        />
      </ColorPicker>

      {/* Name input */}
      <Input
        ref={inputRef}
        value={name}
        onChange={(e) => handleNameChange(e.target.value)}
        onBlur={handleNameBlur}
        onKeyDown={handleKeyDown}
        placeholder={category.isNew ? 'New category name...' : 'Category name'}
        variant="borderless"
        style={{ flex: 1, maxWidth: 200, fontWeight: 500 }}
      />

      {/* Description input */}
      <Input
        value={description}
        onChange={(e) => handleDescriptionChange(e.target.value)}
        placeholder="Description (optional)"
        variant="borderless"
        style={{ flex: 2, color: '#666' }}
      />

      {/* Template count */}
      {!category.isNew && category.templateCount !== undefined && (
        <Text type="secondary" style={{ minWidth: 60, textAlign: 'center' }}>
          {category.templateCount} items
        </Text>
      )}

      {/* Active toggle */}
      {!category.isNew && (
        <Tooltip title={isActive ? 'Active' : 'Inactive'}>
          <Switch
            size="small"
            checked={isActive}
            onChange={handleActiveChange}
          />
        </Tooltip>
      )}

      {/* Status indicator */}
      <div style={{ width: 20, display: 'flex', justifyContent: 'center' }}>
        <StatusIcon />
      </div>

      {/* Delete button */}
      {!category.isNew && (
        <Popconfirm
          title="Delete category?"
          description={
            (category.templateCount ?? 0) > 0
              ? `This category has ${category.templateCount} items. They will be unassigned.`
              : 'This action cannot be undone.'
          }
          onConfirm={() => onDelete(category.id)}
          okText="Delete"
          okButtonProps={{ danger: true }}
        >
          <DeleteOutlined
            style={{
              color: '#999',
              cursor: 'pointer',
              padding: 4,
            }}
          />
        </Popconfirm>
      )}

      {/* Add icon for new row */}
      {category.isNew && (
        <PlusOutlined style={{ color: '#999', padding: 4 }} />
      )}
    </div>
  );
}

export default function CategoriesSettingsPage() {
  const queryClient = useQueryClient();

  // Fetch categories with template counts
  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['categories', 'with-counts'],
    queryFn: () => categoriesApi.listWithCounts(),
  });

  // Update mutation
  const updateCategory = useCallback(
    async (id: string, data: Partial<EditableCategory>) => {
      // Convert null values to undefined for API compatibility
      const apiData: Parameters<typeof categoriesApi.update>[1] = {};
      if (data.name !== undefined) apiData.name = data.name;
      if (data.description !== undefined) apiData.description = data.description || undefined;
      if (data.color !== undefined) apiData.color = data.color;
      if (data.icon !== undefined) apiData.icon = data.icon || undefined;
      if (data.sortOrder !== undefined) apiData.sortOrder = data.sortOrder;
      if (data.isActive !== undefined) apiData.isActive = data.isActive;

      await categoriesApi.update(id, apiData);
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
    [queryClient]
  );

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => categoriesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });

  const handleDelete = useCallback(
    (id: string) => {
      deleteMutation.mutate(id);
    },
    [deleteMutation]
  );

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin />
      </div>
    );
  }

  // Create a "new category" placeholder row
  const newCategoryPlaceholder: EditableCategory = {
    id: 'new',
    tenantId: '',
    name: '',
    description: '',
    color: '#1890ff',
    icon: '',
    sortOrder: categories.length,
    isActive: true,
    createdAt: '',
    updatedAt: '',
    isNew: true,
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0, marginBottom: 4 }}>
          Categories
        </Title>
        <Text type="secondary">
          Organize your work packages into categories. Changes save automatically.
        </Text>
      </div>

      {/* Header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '8px 0',
          borderBottom: '2px solid #f0f0f0',
          color: '#888',
          fontSize: 12,
          fontWeight: 500,
          textTransform: 'uppercase',
        }}
      >
        <div style={{ width: 20 }}>Color</div>
        <div style={{ flex: 1, maxWidth: 200 }}>Name</div>
        <div style={{ flex: 2 }}>Description</div>
        <div style={{ minWidth: 60, textAlign: 'center' }}>Items</div>
        <div style={{ width: 40, textAlign: 'center' }}>Active</div>
        <div style={{ width: 20 }}></div>
        <div style={{ width: 24 }}></div>
      </div>

      {/* Category rows */}
      {categories.length === 0 && (
        <Empty
          description="No categories yet"
          style={{ padding: '40px 0' }}
        />
      )}

      {categories.map((category) => (
        <CategoryRow
          key={category.id}
          category={category}
          onUpdate={updateCategory}
          onDelete={handleDelete}
        />
      ))}

      {/* New category row */}
      <CategoryRow
        key="new"
        category={newCategoryPlaceholder}
        onUpdate={updateCategory}
        onDelete={handleDelete}
        onCreated={() => {
          queryClient.invalidateQueries({ queryKey: ['categories'] });
        }}
      />

      <div style={{ marginTop: 16 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Tip: Click the color dot to change category color. Type a name and press Enter or click away to create.
        </Text>
      </div>
    </div>
  );
}
